/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { ContentEditableEvent, getUrlSync, Text } from '@msdyn365-commerce/core';
import { Address, BusinessPartnerProspect, CommerceProperty } from '@msdyn365-commerce/retail-proxy';
import { createProspectAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import classnames from 'classnames';
import * as React from 'react';
import { IGetAvailableLanguagesData } from '../../actions/get-available-languages.action';
import validateEmailCodeAction, { ValidateEmailCodeInput } from '../../actions/validate-email-code.action';
import validateVatAction, { ValidateVatInput } from '../../actions/validate-vat.action';
import verifyEmailAction, { VerifyEmailInput } from '../../actions/verify-email.action';
import { AddressValidationErrors } from '../cip-business-account-address';
import { ICipBusinessSignUpConfig, ICipBusinessSignUpProps, ICipBusinessSignUpResources } from './cip-business-sign-up.props.autogenerated';
import { SignUpButton, SignUpError, SignUpInput, SignUpLabel } from './components/index';
import { mapProspectType } from './components/prospect-value-mapper';
import SectionHeader from './components/section-header';
import BusinessSignUpDropdown, { IBusinessSignUpDropdownOption } from './components/sign-up-dropdown';
import SignUpEmailVerification from './components/sign-up-email-verification';
import BusinessSignUpRadioButtonOption, { IBusinessSignUpRadioButtonOptionProps } from './components/sign-up-radio-button';
import SignUpTermsConditions from './components/sign-up-terms-conditions';

export interface IBusinessSignUpExtendedProps extends ICipBusinessSignUpProps<ICipBusinessSignUpConfig> {
    formItems?: IFormItem[];
}

export interface IBusinessSignUpItem {
    wrapper: INodeProps;
    isAddressInput?: boolean;
    isValidateVatButton?: boolean;
    isSectionHeader?: boolean;
    id: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input?: React.ReactNode;
    url: string;
}

export interface IBusinessState {
    isReady: boolean;
    formItems: IFormItem[];
    addressHasErrors: boolean;
    showAddressErrors: boolean;
    addressValidationError?: AddressValidationErrors;
    hasSubmitErrors: boolean;
    isContinueWithoutVatId: boolean;
    isVatValidationClicked: boolean;
    isVatCheckExcecuted: boolean;
    isEmailVerificationInProcess: boolean;
    isEmailVerified: boolean;
    isUserVerificationFailed: boolean;
    emailValidationCode: string;
    isSubmittedPressed: boolean;
    isVatCheckFirstClickDone: boolean;
    isAcceptedTermsAndConditions: boolean;
    isUserExisting: boolean;
    hasExistingProspect: boolean;
    isVatIdValid: boolean;
}

export interface IBusinessSignUpLocalAccount {
    FormWrapper: INodeProps;
    items: IBusinessSignUpItem[];
    buttons: React.ReactNode[];
    errorMessage: React.ReactNode[];
    disclaimer: React.ReactNode;
}

export interface IBusinessSignUpViewProps {
    className: string;
    businessSignUp: IModuleProps;
    ContainerWrapper: INodeProps;
    heading: React.ReactNode;
    businessSignUpForm: IBusinessSignUpLocalAccount;
    formItems: IFormItem[];
}

export interface IFormItem {
    id: string;
    type: InputType;
    label: string;
    value: string;
    maxChars?: string;
    isRequired: boolean;
    pattern?: string;
    address?: Address;
    customClass?: string;
    isValid?: boolean;
    fieldReference?: React.RefObject<HTMLInputElement>;
    options?: IBusinessSignUpDropdownOption[];
    selectValue?: IBusinessSignUpDropdownOption | string;
    number?: string;
    radioButtonOptions?: IBusinessSignUpRadioButtonOptionProps[];
    radioButtonGroupClass?: string;
    hasError?: boolean;
}

export enum InputType {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- Text enum doesn't override the imported Text
    Text = 'text',
    Address = 'address',
    Email = 'email',
    TextArea = 'textarea',
    Button = 'button',
    Select = 'select',
    SectionHeader = 'sectionHeader',
    Checkbox = 'checkbox',
    Phone = 'phone',
    VAT = 'vat',
    EmailVerification = 'emailVerification',
    VATLabel = 'vatLabel',
    Country = 'Country',
    RadioButton = 'radioButton',
    Empty = 'empty',
    Language = 'language'
}

export enum InputID {
    FirstName = 'FirstName',
    LastName = 'LastName',
    Email = 'CompanyEmail',
    EmailValidation = 'CompanyEmailValidation',
    EmailVerification = 'EmailVerification',
    CompanyName = 'CompanyName',
    Address = 'CompanyAddress',
    PhoneCode = 'PhoneCode',
    Phone = 'Phone',
    Department = 'Department',
    Comment = 'Comment',
    VAT = 'VAT',
    ValidateVatButton = 'VatButton',
    Country = 'Country',
    Salutation = 'Salutation',
    AdditionalAddressInformation = 'AdditionallAddressInformation',
    ContinueWithoutVAT = 'ContinueWithoutVAT',
    PersonalSectionHeader = 'PersonalSectionHeader',
    EmailSectionHeader = 'EmailSectionHeader',
    CompanySectionHeader = 'CompanySectionHeader',
    VATIdSectionHeader = 'VATIdSectionHeader',
    ConditionsHeader = 'ConditionsHeader',
    Street = 'Street',
    HouseNumber = 'Number',
    ZipCode = 'ZipCode',
    City = 'City',
    Gender = 'Gender',
    ProspectType = 'ProspectType',
    ExistingCustomerNumber = 'ExistingCustomerNumber',
    Empty = 'Empty',
    Language = 'Language'
}

export const renderInputSection = (
    url: string,
    resources: ICipBusinessSignUpResources,
    inputs: IFormItem[],
    className: string,
    needToReset: boolean,
    onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void,
    showErrors: boolean,
    telemetryContent: ITelemetryContent,
    emailValidationCode: string,
    isSubmittedPressed: boolean,
    isUserExisting: boolean,
    hasExistingProspect: boolean,
    addressForm?: React.ReactNode,
    updateAddress?: (errors: boolean, currAddress?: Address, validationErrors?: AddressValidationErrors) => void,
    onButtonClick?: (inputId: string) => void,
    buttonClassName?: string,
    _isContinueWithoutVatId?: boolean,
    _isVatValidationClicked?: boolean,
    _isVatCheckFirstClickDone?: boolean,
    _onIsContinueWithoutVatIdChange?: () => void,
    isEmailVerificationInProcess?: boolean,
    isEmailVerified?: boolean,
    isUserVerificationFailed?: boolean,
    confirmCode?: (code: string) => void,
    _updatePhone?: (newPhone: string) => void,
    _updatePhoneCode?: (newPhoneCode: string) => void,
    _isVatCheckExcecuted?: boolean,
    mappedPropectValue?: string,
    addressValidationError?: AddressValidationErrors,
    languageSlot?: React.ReactNode,
    updateLanguage?: (newChosenLanguage: IGetAvailableLanguagesData) => void
): IBusinessSignUpItem[] => {
    return inputs.map((element, index) => {
        const {
            id,
            type,
            label,
            maxChars,
            isRequired,
            pattern,
            customClass,
            value,
            fieldReference,
            options,
            selectValue,
            number,
            radioButtonOptions,
            radioButtonGroupClass
        } = element;

        const baseSignUpItem: IBusinessSignUpItem = {
            wrapper: {
                className: classnames(className, `${className}-${id}`, customClass)
            },
            id,
            url,
            label: <SignUpLabel {...{ id, forId: id, className, text: label }} />,
            errorMessage: (() => {
                let message = '';
                // Required field generic validation
                if (isSubmittedPressed && isRequired && !value) {
                    message = resources.requiredFieldMissingError;
                }
                // Email verification specific messages
                // For EmailVerification we only surface the generic required-field missing message.
                // All other info/success/failure states are handled inside the SignUpEmailVerification component.
                const variant: 'danger' | 'info' | 'success' = 'danger';
                return <SignUpError {...{ className, type: 'item', message, variant }} />;
            })()
        };

        const addressUiErrors = showErrors || isSubmittedPressed;

        if (type === InputType.Address) {
            if (addressForm) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(addressForm as React.ReactElement, {
                            updateForm: updateAddress,
                            hasError: addressUiErrors,
                            validationError: addressValidationError,
                            showErrors: addressUiErrors,
                            isRequired: isRequired === undefined ? false : isRequired,
                            resetAddress: needToReset,
                            forceValidation: addressUiErrors
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else if (type === InputType.Empty) {
            baseSignUpItem.input = <React.Fragment></React.Fragment>;
        } else if (type === InputType.RadioButton && mappedPropectValue === '0') {
            baseSignUpItem.input = (
                <div className={radioButtonGroupClass}>
                    {radioButtonOptions &&
                        radioButtonOptions.map((element, index) => {
                            return <BusinessSignUpRadioButtonOption key={index} {...element}></BusinessSignUpRadioButtonOption>;
                        })}
                </div>
            );
        } else if (type === InputType.Language) {
            if (languageSlot) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(languageSlot as React.ReactElement, {
                            updateDropdown: updateLanguage,
                            selectedValue: element.selectValue,
                            isSubmittedPressed
                        })}
                    </>
                );
            } else {
                inputs.splice(index, 1);
            }
        } else if (type === InputType.VAT) {
            /*  if (onButtonClick && onIsContinueWithoutVatIdChange) {
                baseSignUpItem.input = (
                    <SignUpVAT
                        {...{
                            resources,
                            id,
                            type,
                            value,
                            className,
                            ariaLabel: 'label',
                            telemetryContent: telemetryContent,
                            text: value,
                            isRequired: isRequired === undefined ? false : isRequired,
                            buttonClassName,
                            onClick: () => onButtonClick(id),
                            isContinueWithoutVatId,
                            isVatCheckFirstClickDone,
                            isVatValidationClicked,
                            handleIscontinueWithoutVatId: () => onIsContinueWithoutVatIdChange(),
                            onChange: onInputChange,
                            isVatValid: isValid,
                            isVatCheckExcecuted,
                            isSubmittedPressed
                        }}
                    />
                );
            } */
        } else if (type === InputType.EmailVerification) {
            if (onButtonClick && confirmCode) {
                baseSignUpItem.input = (
                    <SignUpEmailVerification
                        {...{
                            id,
                            type,
                            value,
                            className,
                            ariaLabel: 'label',
                            telemetryContent: telemetryContent,
                            text: value,
                            isRequired: isRequired === undefined ? false : isRequired,
                            buttonClassName,
                            onClick: () => onButtonClick(InputType.EmailVerification),
                            onConfirmCode: (code: string) => confirmCode(code),
                            onChange: onInputChange,
                            isEmailVerificationInProcess,
                            isEmailVerified,
                            isUserVerificationFailed,
                            emailValidationCode,
                            resources,
                            isSubmittedPressed,
                            isUserExisting,
                            hasExistingProspect,
                            url
                        }}
                    />
                );
            }
        } else if (type !== InputType.Button && type !== InputType.Select && type !== InputType.SectionHeader) {
            baseSignUpItem.input = (
                <SignUpInput
                    {...{
                        id,
                        type,
                        value,
                        maxLength: maxChars,
                        pattern,
                        className,
                        isRequired: isRequired === undefined ? false : isRequired,
                        onChange: onInputChange,
                        fieldReference,
                        isSubmittedPressed
                    }}
                />
            );
        } else if (type === InputType.Button) {
            baseSignUpItem.isValidateVatButton = true;
            if (onButtonClick) {
                baseSignUpItem.input = (
                    // TODO finish button
                    <SignUpButton
                        {...{
                            id,
                            value,
                            className: buttonClassName ?? '',
                            ariaLabel: 'label',
                            telemetryContent: telemetryContent,
                            text: value,
                            onClick: () => onButtonClick(id)
                        }}
                    />
                );
            }
        } else if (type === InputType.Select) {
            if (options) {
                if (typeof selectValue !== 'string') {
                    baseSignUpItem.input = (
                        <BusinessSignUpDropdown
                            id={id}
                            value={value}
                            className={className}
                            isRequired={isRequired}
                            options={options}
                            onChange={(event: any) => onInputChange(event)}
                            selectValue={selectValue}
                            isSubmittedPressed={isSubmittedPressed}
                            resources={resources}
                        />
                    );
                }
            }
        } else if (type === InputType.SectionHeader) {
            baseSignUpItem.isSectionHeader = true;
            baseSignUpItem.input = <SectionHeader header={value} sectionLabel={label} number={number} />;
        }

        return baseSignUpItem;
    });
};

/**
 *
 * Business Request Form Modules
 * Form that a user can use to submit a request to become a business partner.
 * @extends {React.Component<ICipBusinessSignUpProps<ICipBusinessSignUpConfig>>}
 */
class CipBusinessSignUp extends React.Component<IBusinessSignUpExtendedProps, IBusinessState> {
    private readonly prospectRadioButtonName: string = 'business-sign-up-radio-button';
    // Used as default data

    private readonly _onInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLButtonElement | HTMLSelectElement>
    ): void => {
        const newForm = this.state.formItems;

        let isEmailVerified;

        newForm.forEach(item => {
            if (item.type === InputType.RadioButton && event.target.name === this.prospectRadioButtonName) {
                item.value = event.target.value;
            } else if (item.id === event.target.id) {
                item.value = event.target.value;
            }

            if (item?.id === event.target?.id) {
                item.value = event.target.value;

                if (item.type === InputType.Select) {
                    item.selectValue = item.options?.find(el => el.value === event.target.value);
                    if (item.selectValue?.key) {
                        item.value = item.selectValue.value;
                    }
                }

                // A change in the VAT makes it invalid so the user has to validate it again.
                if (item.id === InputID.VAT) {
                    item.value = event.target.value;
                    /*  this.setState({ isVatValidationClicked: false, isVatCheckExcecuted: false });
                    item.isValid = false; */
                }

                // Makes Email invalid again.
                if (item?.id === InputID.EmailVerification) {
                    this.setState({ isEmailVerificationInProcess: false });
                    this.setState({ isEmailVerified: false });
                    this.setState({ isUserVerificationFailed: false });
                    isEmailVerified = false;
                }
            }
        });

        if (event.target?.id === InputID.EmailValidation) {
            this.setState({ emailValidationCode: event.target.value });
        }

        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros

        this.setState({
            isReady:
                event.target.id === InputID.VAT
                    ? false
                    : this._isFormReady(isEmailVerified, this.state.isContinueWithoutVatId, this.state.isVatValidationClicked),
            formItems: newForm,
            hasSubmitErrors: false
        });
    };

    public BusinessSignUpDefaultInputs: IFormItem[] = [
        {
            id: InputID.ProspectType,
            type: InputType.RadioButton,
            label: this.props.resources.prospectTypeLabel,
            value: '',
            isRequired: false,
            customClass: `col-12 ${mapProspectType(this.props.config.prospectType) !== '0' ? 'hidden' : ''}`,
            radioButtonGroupClass: 'cip-business-sign-up__radio-container',
            radioButtonOptions: [
                {
                    id: `${InputID.ProspectType}-buyer`,
                    name: this.prospectRadioButtonName,
                    value: '1', // Buyer
                    label: this.props.resources.prospectTypeBuyerLabel,
                    className: `cip-business-sign-up__form-item-radio`,
                    onChange: this._onInputChange.bind(this)
                },
                {
                    id: `${InputID.ProspectType}-seller`,
                    name: this.prospectRadioButtonName,
                    value: '2', // Seller
                    className: `cip-business-sign-up__form-item-radio`,
                    label: this.props.resources.prospectTypeSellerLabel,
                    onChange: this._onInputChange.bind(this)
                }
            ]
        },
        {
            id: InputID.EmailSectionHeader,
            type: InputType.SectionHeader,
            label: this.props.resources.emailSectionHeaderLabel,
            value: this.props.resources.emailSectionHeader,
            isRequired: false,
            customClass: 'col-12',
            number: '1'
        },
        {
            id: InputID.EmailVerification,
            type: InputType.EmailVerification,
            label: this.props.resources.emailAddressLabel,
            value: '',
            isRequired: true,
            customClass: 'col-12'
        },
        {
            id: InputID.PersonalSectionHeader,
            type: InputType.SectionHeader,
            label: this.props.resources.personalSectionHeaderLabel,
            value: this.props.resources.personalSectionHeader,
            isRequired: false,
            customClass: 'col-12',
            number: '2'
        },
        {
            id: InputID.FirstName,
            type: InputType.Text,
            label: this.props.resources.firstNameLabel,
            value: '',
            isRequired: true,
            customClass: 'col-12 col-md-6'
        },
        {
            id: InputID.LastName,
            type: InputType.Text,
            label: this.props.resources.lastNameLabel,
            value: '',
            isRequired: true,
            customClass: 'col-12 col-md-6'
        },
        {
            id: InputID.Language,
            type: InputType.Language,
            value: '',
            label: this.props.resources.languageSelectLabel,
            isRequired: true,
            options: [],
            selectValue: { key: '', value: '' },
            customClass: 'col-12 col-md-6'
        },
        // Company Data
        {
            id: InputID.CompanySectionHeader,
            type: InputType.SectionHeader,
            label: this.props.resources.companySectionHeaderLabel,
            value: this.props.resources.companySectionHeader,
            isRequired: false,
            customClass: 'col-12',
            number: '3'
        },

        {
            id: InputID.CompanyName,
            type: InputType.Text,
            label: this.props.resources.companyLabel,
            value: '',
            isRequired: true,
            customClass: 'col-12'
        },
        {
            id: InputID.Address,
            type: InputType.Address,
            label: this.props.resources.companyAddressLabel,
            value: '',
            isRequired: false,
            customClass: 'col-12'
        },
        // Empty space for styling
        {
            id: InputID.Empty,
            type: InputType.Empty,
            label: '',
            value: '',
            isRequired: false,
            customClass: 'col-12'
        },
        {
            id: InputID.AdditionalAddressInformation,
            type: InputType.Text,
            label: this.props.resources.additionalAddressLabel,
            value: '',
            isRequired: false,
            customClass: 'col-12'
        },
        {
            id: InputID.ExistingCustomerNumber,
            type: InputType.Text,
            label: this.props.resources.exisitingCustomerNumberLabel,
            value: '',
            isRequired: false,
            customClass: 'col-12'
        },
        {
            id: InputID.VAT,
            type: InputType.Text,
            label: this.props.resources.vatLabel,
            value: '',
            isRequired: false,
            isValid: false,
            customClass: 'col-12'
        },
        {
            id: InputID.ConditionsHeader,
            type: InputType.SectionHeader,
            label: '',
            value: this.props.resources.conditionsSectionHeader,
            isRequired: false,
            customClass: 'col-12',
            number: '4'
        }
    ];

    private readonly formChildReference: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private readonly moduleClassName: string = 'cip-business-sign-up';

    private needsToReset: boolean = false;

    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IBusinessSignUpExtendedProps) {
        super(props);

        this.state = {
            isContinueWithoutVatId: false,
            isVatValidationClicked: false,
            isEmailVerificationInProcess: false,
            isEmailVerified: false,
            isUserVerificationFailed: false,
            isReady: false,
            isVatCheckExcecuted: false,
            isVatCheckFirstClickDone: false,
            isAcceptedTermsAndConditions: false,
            formItems: this.props.formItems || this._addedReference(),
            addressHasErrors: false,
            showAddressErrors: false,
            hasSubmitErrors: false,
            isSubmittedPressed: false,
            emailValidationCode: '',
            isUserExisting: false,
            hasExistingProspect: false,
            isVatIdValid: false
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        this.formChildReference.current?.focus();
    }

    public shouldComponentUpdate(nextProps: IBusinessSignUpExtendedProps, nextState: IBusinessState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { config } = this.props;
        const { formItems } = this.state;
        const { className, heading } = config;

        const viewProps = {
            ...this.props,
            businessSignUp: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, className)
            },
            ContainerWrapper: {
                className: `${this.moduleClassName}__container`
            },
            heading: (
                <Text
                    className={`${this.moduleClassName}__page-heading`}
                    {...heading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            businessSignUpForm: this._renderForm(),
            formItems
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle the heading change event.
     * @param event - Content Editable Event.
     */
    public handleHeadingChange = (event: ContentEditableEvent): void => {
        this.props.config.heading.text = event.target.value;
    };

    private _renderForm(): IBusinessSignUpLocalAccount {
        const { config, resources, slots } = this.props;
        const {
            showAddressErrors,
            hasSubmitErrors,
            isReady,
            formItems,
            isContinueWithoutVatId,
            isEmailVerificationInProcess,
            isEmailVerified,
            isUserVerificationFailed,
            isVatValidationClicked,
            isVatCheckFirstClickDone,
            isVatCheckExcecuted,
            emailValidationCode,
            isSubmittedPressed,
            isUserExisting,
            hasExistingProspect
        } = this.state;
        const {
            signUpButtonArialabel,
            signUpButtonText,
            cancelButtonArialabel,
            cancelButtonText,
            errorMessage,
            requiredFieldMissingSummaryError
        } = resources;

        const addressForm = (slots && slots.addressSlot && slots.addressSlot.length > 0 && slots.addressSlot[0]) || undefined;
        const languageSlot = (slots && slots.languageSlot && slots.languageSlot.length > 0 && slots.languageSlot[0]) || undefined;

        const successPageURL =
            (config.confirmationLink && config.confirmationLink.linkUrl && config.confirmationLink.linkUrl.destinationUrl) ||
            getUrlSync('home', this.props.context.actionContext);

        const url = this.props.context.request.user.signInUrl || '';
        const mappedPropectValue = mapProspectType(config.prospectType);
        const form = {
            FormWrapper: {
                className: 'row'
            },
            items: renderInputSection(
                url,
                resources,
                formItems,
                `${this.moduleClassName}__form-item`,
                this.needsToReset,
                this._onInputChange.bind(this),
                showAddressErrors,
                this.telemetryContent,
                emailValidationCode,
                isSubmittedPressed,
                isUserExisting,
                hasExistingProspect,
                addressForm,
                this._onAddressChange,
                this._onButtonClick,
                this.moduleClassName,
                isContinueWithoutVatId,
                isVatValidationClicked,
                isVatCheckFirstClickDone,
                this._onIsContinueWithoutVatIdChange,
                isEmailVerificationInProcess,
                isEmailVerified,
                isUserVerificationFailed,
                this._confirmEmailCode,
                this._updatePhone,
                this._updatePhoneCode,
                isVatCheckExcecuted,
                mappedPropectValue,
                this.state.addressValidationError,
                languageSlot,
                this._updateLanguage
            ),
            buttons: [
                <SignUpButton
                    key='continue'
                    id='continue'
                    className={`${
                        !isReady ? `${this.moduleClassName}__sign-up-button-disabled` : `${this.moduleClassName}__sign-up-button`
                    }`}
                    link={successPageURL}
                    ariaLabel={signUpButtonArialabel}
                    text={signUpButtonText}
                    onClick={this._onSubmit}
                    telemetryContent={this.telemetryContent}
                />,
                <SignUpButton
                    key='cancel'
                    id='cancel'
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={cancelButtonArialabel}
                    text={cancelButtonText}
                    onClick={this._onCancel}
                    telemetryContent={this.telemetryContent}
                />
            ],
            errorMessage: [
                hasSubmitErrors && <SignUpError key='error' id='error' className={this.moduleClassName} message={errorMessage} />,
                showAddressErrors && (
                    <SignUpError key='error' id='error' className={this.moduleClassName} message={requiredFieldMissingSummaryError} />
                )
            ],
            disclaimer: [
                <SignUpTermsConditions
                    key='terms-and-conditions'
                    resources={resources}
                    id='termsAndConditions'
                    type='checkbox'
                    name='termsAndConditions'
                    className={this.moduleClassName}
                    isAcceptedTermsAndConditions={this.state.isAcceptedTermsAndConditions}
                    onChange={this._onIsAcceptedTermsAndConditions}
                    isSubmittedPressed={isSubmittedPressed}
                />
            ]
        };

        if (this.needsToReset) {
            this.needsToReset = false;
        }

        return form;
    }
    private _addedReference(): IFormItem[] {
        const newFormFields = this.BusinessSignUpDefaultInputs;
        newFormFields[0].fieldReference = this.formChildReference;
        return newFormFields;
    }

    private readonly _onAddressChange = (errors: boolean, currentAddress?: Address, validationErrors?: AddressValidationErrors): void => {
        // Find address item
        const newForm = this.state.formItems;
        const addressItem = newForm.find(item => {
            return item.type === InputType.Address;
        });

        // If found --> set to currentAddress
        if (addressItem) {
            addressItem.address = currentAddress;

            // Update state with new values and set errors to false if address is not required field
            this.setState({
                isReady: this._isFormReady(
                    this.state.isEmailVerified,
                    this.state.isContinueWithoutVatId,
                    this.state.isVatValidationClicked
                ),
                formItems: newForm,
                addressHasErrors: errors,
                showAddressErrors: errors ? this.state.isSubmittedPressed || this.state.showAddressErrors : false,
                addressValidationError: validationErrors
            });
        }
    };

    private readonly _onIsContinueWithoutVatIdChange = () => {
        this.setState({
            isReady: this._isFormReady(this.state.isEmailVerified, !this.state.isContinueWithoutVatId, this.state.isVatValidationClicked),
            isContinueWithoutVatId: !this.state.isContinueWithoutVatId
        });
    };

    private readonly _onIsAcceptedTermsAndConditions = () => {
        this.setState(
            {
                isAcceptedTermsAndConditions: !this.state.isAcceptedTermsAndConditions
            },
            () => {
                this._changeIsAcceptedTermsAndConditions();
            }
        );
    };

    private readonly _changeIsAcceptedTermsAndConditions = () => {
        this.setState({
            isReady: this._isFormReady(this.state.isEmailVerified, this.state.isContinueWithoutVatId, this.state.isVatValidationClicked)
        });
    };

    private readonly _onButtonClick = (id: string) => {
        if (id === InputType.EmailVerification) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._onVerifyEmailClick();
        } else {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._onValidateVATClick();
        }
    };

    private readonly _onValidateVATClick = async () => {
        const vatId = this._getItem(InputID.VAT);
        const companyName = this._getItem(InputID.CompanyName);
        if (vatId) {
            const newFormItems = this.state.formItems;
            const actionInput = new ValidateVatInput(vatId.value, companyName?.value ?? '');
            await validateVatAction(actionInput, this.props.context.actionContext).then(res => {
                newFormItems.forEach(el => {
                    if (el.id === InputID.VAT) {
                        el.isValid = res.response.isValid;
                    }
                });
                this.setState({
                    formItems: newFormItems,
                    isVatCheckExcecuted: res.response.isVatCheckExcecuted ?? false,
                    isVatIdValid: res.response.isValid
                });
            });
        }

        this.setState({
            isReady: this._isFormReady(this.state.isEmailVerified, this.state.isContinueWithoutVatId, true),
            isVatValidationClicked: true,
            isVatCheckFirstClickDone: true
        });
    };

    private readonly _onVerifyEmailClick = async () => {
        const emailAddress = this._getItem(InputID.EmailVerification);
        const channelNaturalId = this.props.context.request?.channel?.ChannelNaturalId || '';
        if (emailAddress) {
            const actionInput = new VerifyEmailInput(emailAddress.value, channelNaturalId);
            await verifyEmailAction(actionInput, this.props.context.actionContext).then(res => {
                this.setState({ isEmailVerificationInProcess: res.response, isUserVerificationFailed: false });
            });
        }
    };

    private readonly _confirmEmailCode = async (code: string) => {
        const emailAddress = this._getItem(InputID.EmailVerification);
        if (emailAddress) {
            const actionInput = new ValidateEmailCodeInput(emailAddress.value, code);
            await validateEmailCodeAction(actionInput, this.props.context.actionContext).then((res: any) => {
                if (!res.validCode) {
                    this.setState({ isUserVerificationFailed: true });
                } else if (res.validCode && res.isUserExisting) {
                    this.setState({
                        isUserVerificationFailed: true,
                        isUserExisting: true
                    });
                } else if (res.validCode && !res.isUserExisting) {
                    this.setState({
                        isUserVerificationFailed: res.existingProspect,
                        isUserExisting: false,
                        isEmailVerified: !res.existingProspect,
                        hasExistingProspect: res.existingProspect
                    });
                }
                this.setState({
                    isReady: this._isFormReady(
                        !this.state.isUserVerificationFailed,
                        this.state.isContinueWithoutVatId,
                        this.state.isVatValidationClicked
                    )
                });
            });
        }
    };

    private readonly _updateLanguage = (newChosenLanguage: IGetAvailableLanguagesData) => {
        // Find address item
        const newForm = this.state.formItems;
        const languageItem = newForm.find(item => {
            return item.type === InputType.Language;
        });

        // If found --> set to currentAddress
        if (languageItem) {
            languageItem.value = newChosenLanguage.value;
            languageItem.selectValue = newChosenLanguage;

            // Update state with new values and set errors to false if address is not required field
            this.setState({
                isReady: this._isFormReady(
                    this.state.isEmailVerified,
                    this.state.isContinueWithoutVatId,
                    this.state.isVatValidationClicked
                ),
                formItems: newForm
            });
        }
    };

    private readonly _updatePhone = (newInput: string) => {
        const newForm = this.state.formItems;
        const phoneItem = newForm.find(item => {
            return item.type === InputType.Phone;
        });
        if (phoneItem) {
            phoneItem.value = newInput;
            this.setState({
                isReady: this._isFormReady(
                    this.state.isEmailVerified,
                    this.state.isContinueWithoutVatId,
                    this.state.isVatValidationClicked
                ),
                formItems: newForm
            });
        }
    };
    private readonly _updatePhoneCode = (newChosenPhoneCode: string) => {
        const newForm = this.state.formItems;
        const phoneCodeItem = newForm.find(item => {
            return item.type === InputType.Phone;
        });

        if (phoneCodeItem) {
            phoneCodeItem.selectValue = newChosenPhoneCode;
            this.setState({
                isReady: this._isFormReady(
                    this.state.isEmailVerified,
                    this.state.isContinueWithoutVatId,
                    this.state.isVatValidationClicked
                ),
                formItems: newForm
            });
        }
    };

    private readonly _isFormReady = (
        _isEmailVerified?: boolean,
        _isContinueWithoutVatId?: boolean,
        _isVatValidationClicked?: boolean
    ): boolean => {
        const { formItems } = this.state;
        for (const currentInput of formItems) {
            // Will return FALSE if:
            //  - input field is required
            //      IF is address AND addressHasErrors === true
            //      ELSE value is undefined OR empty
            //  OR
            //  - pattern provided and value does not match pattern
            // OR
            // - accepts terms and conditions checkbox is not checked

            if (currentInput.isRequired) {
                if (currentInput.type === InputType.Address) {
                    if (this.state.addressHasErrors) {
                        return false;
                    }
                } else if (currentInput.value === undefined || currentInput.value === '') {
                    return false;
                }
                if (currentInput.id === InputID.Phone) {
                    if (currentInput.value === '' || currentInput.selectValue === '+' || currentInput.selectValue === undefined) {
                        return false;
                    }
                }
            }
            if (currentInput.id === InputID.VAT) {
                if (currentInput.value === undefined || currentInput.value === '') {
                    this.setState({ isContinueWithoutVatId: true });
                } else {
                    this.setState({ isContinueWithoutVatId: false });
                }

                /*  if (!(!currentInput.isValid && isVatValidationClicked && !this.state.isVatCheckExcecuted)) {
                    if (!currentInput.isValid) {
                        if (!isContinueWithoutVatId) {
                            return false;
                        }
                    }
                } */
            }

            if (currentInput.pattern !== undefined && currentInput.value.match(currentInput.pattern) === null) {
                return false;
            }
        }
        if (_isEmailVerified !== undefined && _isEmailVerified === false) {
            return false;
        } else if (!this.state.isEmailVerified) {
            return false;
        }
        if (!this.state.isAcceptedTermsAndConditions) {
            return false;
        }
        return true;
    };

    private readonly _onSubmit = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        this.setState({ isSubmittedPressed: true });
        // If form not ready, prevent navigation and surface item level errors
        if (!this.state.isReady) {
            event.preventDefault();
            this.setState({
                // Do not surface the generic page-level error when the form is invalid.
                // Keep page-level errors reserved for server/network failures.
                hasSubmitErrors: false,
                showAddressErrors: this.state.addressHasErrors || this.state.showAddressErrors
            });
            return;
        }
        if (this.state.isReady) {
            if (this.state.addressHasErrors) {
                this.setState({
                    showAddressErrors: true
                });

                event.preventDefault();
                return;
            }

            const { context, config } = this.props;

            // Check for matching id and if not empty
            const firstName = this._getItem(InputID.FirstName);
            const lastName = this._getItem(InputID.LastName);
            const companyName = this._getItem(InputID.CompanyName);
            const companyEmail = this._getItem(InputID.EmailVerification);
            const comments = this._getItem(InputID.Comment);
            const companyAddress = this._getItem(InputID.Address, true);
            const additionalAddressInformation = this._getItem(InputID.AdditionalAddressInformation);
            const existingCustomerNumber = this._getItem(InputID.ExistingCustomerNumber);
            const vatId = this._getItem(InputID.VAT);
            const language = this._getItem(InputID.Language);

            /* const isVatIdValid = this.state.isVatIdValid;
            const isVatCheckExcecuted = this.state.isVatCheckExcecuted; */

            if (companyEmail && companyEmail.value && companyAddress && companyAddress.address) {
                companyAddress.address.Email = companyEmail.value;
            }

            const ExtensionProperties: CommerceProperty[] = [
                {
                    Key: 'AdditionalInfo',
                    Value: {
                        StringValue: additionalAddressInformation?.value
                    }
                },
                {
                    Key: 'ExistingCustomerAccount',
                    Value: {
                        StringValue: existingCustomerNumber?.value
                    }
                },
                {
                    Key: 'VatId',
                    Value: {
                        StringValue: vatId?.value
                    }
                },
                {
                    Key: 'Language',
                    Value: {
                        StringValue: typeof language?.selectValue === 'object' ? language?.selectValue?.key : ''
                    }
                }
                /* {
                    Key: 'VatIdIsValid',
                    Value: {
                        BooleanValue: isVatIdValid
                    }
                } */
                /* {
                    Key: 'IsVatCheckExcecuted',
                    Value: {
                        BooleanValue: isVatCheckExcecuted
                    }
                } */
            ];

            const prospectTypeItem = this._getItem(InputID.ProspectType);
            const prospectTypeValue = prospectTypeItem?.value || mapProspectType(config.prospectType);

            const newPartner: BusinessPartnerProspect = {
                FirstName: firstName && firstName.value,
                LastName: lastName && lastName.value,
                CompanyName: companyName && companyName.value,
                Address: companyAddress && companyAddress.address && companyAddress.address,
                Comments: comments && comments.value,
                ExtensionProperties: ExtensionProperties,
                ProspectTypeValue: Number.parseInt(prospectTypeValue, 10)
            };

            // Prevent the a href in order to execute the async request first (firefox specific workaround)
            event.preventDefault();

            // Create business partner request
            createProspectAsync({ callerContext: context.actionContext }, newPartner)
                .catch(() => {
                    this.setState({
                        hasSubmitErrors: true,
                        isReady: false
                    });
                })
                .finally(() => {
                    const config = this.props.config;
                    window.location.href =
                        (config.confirmationLink && config.confirmationLink.linkUrl && config.confirmationLink.linkUrl.destinationUrl) ||
                        getUrlSync('home', this.props.context.actionContext) ||
                        '';
                });
        }
    };

    private readonly _onCancel = () => {
        window.history.back();
    };

    private readonly _getItem = (id: string, isAddress: boolean = false) => {
        if (isAddress) {
            return this.state.formItems.find(item => {
                return item.id === id && item.address;
            });
        }
        return this.state.formItems.find(item => {
            if (item?.id === id) {
                if (id === InputID.Phone) {
                    return item;
                }
                return item.value !== '';
            } else {
                return undefined;
            }
        });
    };
}

export default CipBusinessSignUp;
